import { type ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '../services/auth.service';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorCode, HARD_LOGOUT_CODES } from '@/lib/api/auth-codes';

interface SessionGateProps {
  children: ReactNode;
}

export const SESSION_PROBE_KEY = ['auth', 'session-probe'] as const;

export function SessionGate({ children }: SessionGateProps) {
  const token = useAuthStore((s) => s.accessToken);
  const setPermissions = useAuthStore((s) => s.setPermissions);
  const hasToken = !!token;

  // Re-fetch the current user's profile on focus + a light poll so a
  // permission/role change made by an admin propagates to this session
  // within ~60s without requiring a re-login.
  const { isLoading, isError, error, data } = useQuery({
    queryKey: SESSION_PROBE_KEY,
    queryFn: () => authService.getProfile(),
    enabled: hasToken,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  // Keep the auth store's effective permissions in sync with the freshest
  // profile. We deliberately update ONLY permissions (not the whole user):
  // get-profile returns the raw backend shape where `roles` are objects, while
  // the store's `user.roles` holds normalized EUserRole strings.
  useEffect(() => {
    if (data) setPermissions(data.permissions ?? []);
  }, [data, setPermissions]);

  if (!hasToken) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="space-y-3 w-64">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
        </div>
      </div>
    );
  }

  if (isError) {
    const code = extractErrorCode(error);
    if (code && HARD_LOGOUT_CODES.has(code)) {
      // Interceptor is performing hard-logout + redirect; render nothing.
      return null;
    }
  }

  return <>{children}</>;
}
